/**
 * Assign Inspector Page
 * Admin can assign an inspector to a property with calendar view
 */

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Grid,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { adminAPI } from "utils/api";

const localizer = momentLocalizer(moment);

function AssignInspector() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State
  const [property, setProperty] = useState(null);
  const [inspectors, setInspectors] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState("");
  const [inspectorCalendar, setInspectorCalendar] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [calendarView, setCalendarView] = useState("week");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [hoverModalOpen, setHoverModalOpen] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  // Form data
  const [formData, setFormData] = useState({
    inspectionDate: "",
    inspectionLocation: "",
  });

  // Initialize inspection location with property address and Google Maps link
  useEffect(() => {
    const addressValue = property?.address?.en || property?.address || "";
    if (property && addressValue) {
      const googleMapsLink = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        addressValue
      )}`;
      const addressText = property.address?.en || property.address || "";
      const cityText = property.city?.en || property.city || "";
      const locationText = `${addressText}${
        cityText ? `, ${cityText}` : ""
      }\n\nGoogle Maps: ${googleMapsLink}`;
      setFormData((prev) => {
        // Only auto-fill if location is empty
        if (!prev.inspectionLocation || prev.inspectionLocation.trim() === "") {
          return {
            ...prev,
            inspectionLocation: locationText,
          };
        }
        return prev;
      });
    }
  }, [property]);

  // Fetch property data
  useEffect(() => {
    const fetchProperty = async () => {
      try {
        const response = await adminAPI.getPropertyById(id);
        setProperty(response.property);
      } catch (error) {
        console.error("Error fetching property:", error);
        setMessage({
          type: "error",
          text: "Failed to load property. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProperty();
    }
  }, [id]);

  // Fetch available inspectors
  useEffect(() => {
    const fetchInspectors = async () => {
      try {
        const response = await adminAPI.getAvailableInspectors();
        setInspectors(response.inspectors || []);
      } catch (error) {
        console.error("Error fetching inspectors:", error);
        setMessage({
          type: "error",
          text: "Failed to load inspectors. Please try again.",
        });
      }
    };

    fetchInspectors();
  }, []);

  // Fetch inspector calendar when inspector is selected
  useEffect(() => {
    const fetchInspectorCalendar = async () => {
      if (!selectedInspector) {
        setInspectorCalendar(null);
        setSelectedSlot(null);
        return;
      }

      try {
        const response = await adminAPI.getInspectorCalendar(selectedInspector);
        setInspectorCalendar(response);
        setSelectedSlot(null);
        setFormData((prev) => ({ ...prev, inspectionDate: "" }));
      } catch (error) {
        console.error("Error fetching inspector calendar:", error);
        setMessage({
          type: "error",
          text: "Failed to load inspector calendar. Please try again.",
        });
      }
    };

    fetchInspectorCalendar();
  }, [selectedInspector]);

  // Helper function to format date for datetime-local input (local time)
  const formatDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Check if selected date conflicts with inspector's schedule
  const checkDateConflict = (selectedDate, selectedEndDate) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections || !selectedDate) {
      return null;
    }

    const selectedDateTime = new Date(selectedDate);
    const selectedEndTime = selectedEndDate
      ? new Date(selectedEndDate)
      : new Date(selectedDateTime);

    const conflict = inspectorCalendar.inspections.find((inspection) => {
      const inspectionDate = new Date(inspection.inspectionDate);
      // Use a 1-hour default duration for existing inspections (just for conflict checking)
      const inspectionEndTime = new Date(inspectionDate);
      inspectionEndTime.setHours(inspectionEndTime.getHours() + 1);

      // Check if there's an overlap
      return (
        (selectedDateTime >= inspectionDate && selectedDateTime < inspectionEndTime) ||
        (selectedEndTime > inspectionDate && selectedEndTime <= inspectionEndTime) ||
        (selectedDateTime <= inspectionDate && selectedEndTime >= inspectionEndTime)
      );
    });

    return conflict || null;
  };

  // Helper function to get multilingual text
  const getMultilingualText = (text, defaultText = "") => {
    if (!text) return defaultText;
    if (typeof text === "string") return text;
    if (typeof text === "object" && text.en) return text.en;
    if (typeof text === "object" && text.ar) return text.ar;
    return defaultText;
  };

  // Convert inspector inspections to calendar events
  const getCalendarEvents = () => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) return [];

    const events = inspectorCalendar.inspections.map((inspection) => {
      const startDate = new Date(inspection.inspectionDate);
      // Use 1-hour duration for display purposes only
      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1);

      // Format time for display
      const timeStr = startDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      // Handle multilingual title
      const inspectionTitle = getMultilingualText(
        inspection.title,
        inspection.propertyTitle || `Inspection - ${timeStr}`
      );

      return {
        title: inspectionTitle,
        start: startDate,
        end: endDate,
        resource: {
          id: inspection.id,
          title: inspectionTitle,
          address: getMultilingualText(inspection.address, inspection.propertyAddress || "N/A"),
          city: getMultilingualText(inspection.city, inspection.propertyCity || "N/A"),
          location: inspection.inspectionLocation,
          status: inspection.status,
          isExisting: true, // Mark as existing inspection
        },
      };
    });

    // Add selected slot as a temporary event for visual feedback
    if (selectedSlot) {
      events.push({
        title: "Selected Time",
        start: selectedSlot.start,
        end: selectedSlot.end,
        resource: {
          id: "selected",
          isSelected: true,
        },
      });
    }

    return events;
  };

  // Check if a slot is reserved
  const isSlotReserved = (slotStart, slotEnd) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) {
      return false;
    }

    return inspectorCalendar.inspections.some((inspection) => {
      const inspectionStart = new Date(inspection.inspectionDate);
      const inspectionEnd = new Date(inspectionStart);
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);

      // Check for overlap
      return (
        (slotStart >= inspectionStart && slotStart < inspectionEnd) ||
        (slotEnd > inspectionStart && slotEnd <= inspectionEnd) ||
        (slotStart <= inspectionStart && slotEnd >= inspectionEnd)
      );
    });
  };

  // Handle calendar slot selection
  const handleSelectSlot = ({ start, end }) => {
    // Use the start time from the calendar (this is the actual clicked slot)
    const clickedTime = new Date(start);

    // Round to the nearest hour (set minutes and seconds to 0)
    const selectedStart = new Date(clickedTime);
    selectedStart.setMinutes(0, 0, 0);

    // Set end time to exactly 1 hour after start
    const selectedEnd = new Date(selectedStart);
    selectedEnd.setHours(selectedEnd.getHours() + 1);

    // Check if the slot is already reserved
    if (isSlotReserved(selectedStart, selectedEnd)) {
      setMessage({
        type: "error",
        text: `This time slot (${selectedStart.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })} - ${selectedEnd.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })}) is already reserved. Please choose a different time.`,
      });
      setSelectedSlot(null);
      return;
    }

    // Check if the selected slot conflicts with existing inspections (double check)
    const conflict = checkDateConflict(selectedStart.toISOString(), selectedEnd.toISOString());
    if (conflict) {
      setMessage({
        type: "error",
        text: `This time slot conflicts with an existing inspection at ${new Date(
          conflict.inspectionDate
        ).toLocaleString()}. Please choose a different time.`,
      });
      setSelectedSlot(null);
      return;
    }

    // Format for datetime-local input (use start time in local timezone)
    // datetime-local expects format: YYYY-MM-DDTHH:mm in local time
    const formattedDateTime = formatDateTimeLocal(selectedStart);

    // Store selected slot for visual feedback
    setSelectedSlot({
      start: selectedStart,
      end: selectedEnd,
    });

    // Close any open modal when selecting a new slot
    setHoverModalOpen(false);

    setFormData((prev) => ({
      ...prev,
      inspectionDate: formattedDateTime,
    }));

    setMessage({
      type: "success",
      text: `Time selected: ${selectedStart.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })} - ${selectedEnd.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })}`,
    });
  };

  // Handle calendar event click
  const handleSelectEvent = (event) => {
    // Prevent clicking on existing inspections
    if (event.resource?.isExisting) {
      setMessage({
        type: "info",
        text: `This time slot is already reserved for: ${event.resource.title || "Inspection"}`,
      });
      return;
    }

    if (event.resource?.isSelected) {
      // Clicking on selected slot - deselect it
      setSelectedSlot(null);
      setFormData((prev) => ({ ...prev, inspectionDate: "" }));
      setMessage({ type: "", text: "" });
    }
  };

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Update selected slot when date is manually changed
    if (name === "inspectionDate" && value) {
      // datetime-local input gives us local time, so we can use it directly
      const selectedDateTime = new Date(value);
      // Round to the hour
      selectedDateTime.setMinutes(0, 0, 0);
      const endDateTime = new Date(selectedDateTime);
      endDateTime.setHours(endDateTime.getHours() + 1);
      setSelectedSlot({
        start: selectedDateTime,
        end: endDateTime,
      });
    } else if (name === "inspectionDate" && !value) {
      setSelectedSlot(null);
    }
  };

  // Handle inspector selection
  const handleInspectorChange = (event) => {
    const inspectorId = event.target.value;

    if (inspectorId && inspectorId !== "" && inspectorId !== "undefined") {
      // Ensure we store as string for consistency
      const idString = String(inspectorId);
      setSelectedInspector(idString);
      setSelectedSlot(null);
      setFormData((prev) => ({ ...prev, inspectionDate: "" }));
      setMessage({ type: "", text: "" });
    }
  };

  // Handle view change
  const handleViewChange = (view) => {
    setCalendarView(view);
  };

  // Navigate calendar
  const navigateCalendar = (action) => {
    const newDate = new Date(calendarDate);
    if (calendarView === "month") {
      newDate.setMonth(
        action === "PREV" ? calendarDate.getMonth() - 1 : calendarDate.getMonth() + 1
      );
    } else if (calendarView === "week") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 7 : calendarDate.getDate() + 7);
    } else if (calendarView === "day") {
      newDate.setDate(action === "PREV" ? calendarDate.getDate() - 1 : calendarDate.getDate() + 1);
    }
    setCalendarDate(newDate);
  };

  // Check if a time slot is disabled (reserved/booked)
  const slotPropGetter = (date) => {
    if (!inspectorCalendar || !inspectorCalendar.inspections) {
      return {};
    }

    // Round to the hour for consistent checking
    const slotStart = new Date(date);
    slotStart.setMinutes(0, 0, 0);
    const slotEnd = new Date(slotStart);
    slotEnd.setHours(slotEnd.getHours() + 1);

    // Check if this slot conflicts with any existing inspection
    const hasConflict = inspectorCalendar.inspections.some((inspection) => {
      const inspectionStart = new Date(inspection.inspectionDate);
      inspectionStart.setMinutes(0, 0, 0);
      const inspectionEnd = new Date(inspectionStart);
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);

      // Check for overlap - any overlap means the slot is reserved
      return (
        (slotStart.getTime() >= inspectionStart.getTime() &&
          slotStart.getTime() < inspectionEnd.getTime()) ||
        (slotEnd.getTime() > inspectionStart.getTime() &&
          slotEnd.getTime() <= inspectionEnd.getTime()) ||
        (slotStart.getTime() <= inspectionStart.getTime() &&
          slotEnd.getTime() >= inspectionEnd.getTime())
      );
    });

    if (hasConflict) {
      return {
        className: "rbc-slot-reserved",
        style: {
          backgroundColor: "rgba(244, 67, 54, 0.15)",
          border: "1px dashed rgba(244, 67, 54, 0.5)",
          cursor: "not-allowed",
          pointerEvents: "none",
          opacity: 0.7,
        },
      };
    }

    return {};
  };

  // Event style getter
  const eventStyleGetter = (event) => {
    // Selected slot (new selection)
    if (event.resource?.isSelected) {
      return {
        style: {
          backgroundColor: "#4CAF50",
          borderRadius: "6px",
          opacity: 1,
          color: "white",
          border: "2px solid #2e7d32",
          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.5)",
          display: "flex",
          alignItems: "center",
          padding: "8px 12px",
          fontWeight: 600,
          fontSize: "0.875rem",
          minHeight: "32px",
          zIndex: 10,
          position: "relative",
          whiteSpace: "normal",
          wordWrap: "break-word",
          lineHeight: "1.4",
          cursor: "pointer",
        },
      };
    }

    // Existing inspections (not clickable) - truncate text
    if (event.resource?.isExisting) {
      return {
        style: {
          backgroundColor: "#1976d2",
          borderRadius: "6px",
          opacity: 0.95,
          color: "white",
          border: "none",
          display: "flex",
          alignItems: "center",
          padding: "6px 10px",
          fontWeight: 500,
          fontSize: "0.875rem",
          minHeight: "28px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: "1.4",
          cursor: "pointer",
          pointerEvents: "auto",
        },
      };
    }

    // Default style
    const style = {
      backgroundColor: "#1976d2",
      borderRadius: "6px",
      opacity: 0.95,
      color: "white",
      border: "none",
      display: "flex",
      alignItems: "center",
      padding: "6px 10px",
      fontWeight: 500,
      fontSize: "0.875rem",
      minHeight: "28px",
      whiteSpace: "normal",
      wordWrap: "break-word",
      lineHeight: "1.4",
    };
    return { style };
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedInspector || !formData.inspectionDate || !formData.inspectionLocation.trim()) {
      setMessage({
        type: "error",
        text: "Please fill in all required fields: Inspector, Date, and Location.",
      });
      return;
    }

    // Check for conflicts using selected slot times if available
    // formData.inspectionDate is in local time format (YYYY-MM-DDTHH:mm)
    // Convert it to a Date object (it will be interpreted as local time)
    const inspectionStart = new Date(formData.inspectionDate);
    // Ensure it's rounded to the hour
    inspectionStart.setMinutes(0, 0, 0);
    const inspectionEnd = selectedSlot?.end
      ? new Date(selectedSlot.end)
      : new Date(inspectionStart);

    // If no end time from selected slot, use 1 hour default
    if (!selectedSlot?.end || inspectionEnd.getTime() === inspectionStart.getTime()) {
      inspectionEnd.setHours(inspectionEnd.getHours() + 1);
    }

    const conflict = checkDateConflict(inspectionStart.toISOString(), inspectionEnd.toISOString());
    if (conflict) {
      setMessage({
        type: "error",
        text: `Inspector is already assigned to another inspection at ${new Date(
          conflict.inspectionDate
        ).toLocaleString()}. Please choose a different time.`,
      });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: "", text: "" });

      // Convert local datetime to ISO string for backend (backend expects ISO format)
      // formData.inspectionDate is in format "YYYY-MM-DDTHH:mm" (local time)
      // When we create a Date object from this, it interprets it as local time
      // Then toISOString() converts it to UTC for the backend
      const localDate = new Date(formData.inspectionDate);

      // Validate the date is valid
      if (isNaN(localDate.getTime())) {
        setMessage({
          type: "error",
          text: "Invalid date format. Please select a valid date and time.",
        });
        setSubmitting(false);
        return;
      }

      const isoDateString = localDate.toISOString();

      const response = await adminAPI.assignInspector(id, {
        inspectorId: selectedInspector,
        inspectionDate: isoDateString,
        inspectionLocation: formData.inspectionLocation.trim(),
      });

      if (response.message) {
        setMessage({
          type: "success",
          text: response.message,
        });

        // Redirect to property details after 2 seconds
        setTimeout(() => {
          navigate(`/admin/properties/${id}`);
        }, 2000);
      }
    } catch (error) {
      console.error("Error assigning inspector:", error);
      setMessage({
        type: "error",
        text: error.message || "Failed to assign inspector. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  if (!property) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Alert severity="error">Property not found.</Alert>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => navigate("/admin/properties")}
            sx={{ mt: 2 }}
          >
            Back to Properties
          </MDButton>
        </MDBox>
      </DashboardLayout>
    );
  }

  const selectedInspectorData = inspectors.find(
    (insp) => String(insp._id || insp.id) === String(selectedInspector)
  );
  const dateConflict = formData.inspectionDate
    ? checkDateConflict(
        new Date(formData.inspectionDate).toISOString(),
        selectedSlot?.end
          ? selectedSlot.end.toISOString()
          : new Date(
              new Date(formData.inspectionDate).setHours(
                new Date(formData.inspectionDate).getHours() + 1
              )
            ).toISOString()
      )
    : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDButton
              variant="outlined"
              color="info"
              onClick={() => navigate(`/admin/properties/${id}`)}
              startIcon={<Icon>arrow_back</Icon>}
              sx={{ mb: 2 }}
            >
              Back to Property Details
            </MDButton>
            <MDTypography variant="h4" fontWeight="bold">
              Assign Inspector
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Schedule an inspection for: {property.title?.en || property.title || "N/A"}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Message Alert */}
        {message.text && (
          <Alert
            severity={message.type === "error" ? "error" : "success"}
            sx={{ mb: 3 }}
            onClose={() => setMessage({ type: "", text: "" })}
          >
            {message.text}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Property Info & Form */}
          <Grid item xs={12} lg={4}>
            {/* Property Summary Card */}
            <Card sx={{ mb: 3 }}>
              <MDBox p={3}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>home</Icon>
                  Property Information
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                <MDBox mb={2}>
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    Property Title
                  </MDTypography>
                  <MDTypography variant="body1" fontWeight="medium">
                    {property.title?.en || property.title || "N/A"}
                  </MDTypography>
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    Address
                  </MDTypography>
                  <MDTypography variant="body1">
                    {property.address?.en || property.address || "N/A"}
                  </MDTypography>
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="caption" color="text" fontWeight="medium">
                    City
                  </MDTypography>
                  <MDTypography variant="body1">
                    {property.city?.en || property.city || "N/A"}
                  </MDTypography>
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontWeight="medium"
                    display="block"
                    mb={1}
                  >
                    Status
                  </MDTypography>
                  <Chip
                    icon={
                      <Icon
                        sx={{
                          fontSize: "16px !important",
                          color: "inherit",
                        }}
                      >
                        {["Pending", "Request_Submission"].includes(property.status)
                          ? "schedule"
                          : ["Approved", "Active"].includes(property.status)
                          ? "check_circle"
                          : ["Inspection", "First_Inspection"].includes(property.status)
                          ? "search"
                          : ["Inspection_Offer", "Inspection_Completed"].includes(property.status)
                          ? "task_alt"
                          : property.status === "Rejected"
                          ? "cancel"
                          : "info"}
                      </Icon>
                    }
                    label={property.status || "Pending"}
                    color={
                      ["Pending", "Request_Submission"].includes(property.status)
                        ? "error"
                        : ["Approved", "Active"].includes(property.status)
                        ? "success"
                        : [
                            "Inspection",
                            "First_Inspection",
                            "Inspection_Offer",
                            "Inspection_Completed",
                          ].includes(property.status)
                        ? "info"
                        : property.status === "Rejected"
                        ? "error"
                        : "default"
                    }
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      height: "28px",
                      "& .MuiChip-icon": {
                        marginLeft: "8px",
                      },
                    }}
                  />
                </MDBox>
                {property.address && (
                  <MDBox mt={2}>
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        property.address
                      )}`}
                      target="_blank"
                      startIcon={<Icon>map</Icon>}
                      fullWidth
                    >
                      View on Google Maps
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>
            </Card>

            {/* Ready for inspection, preferred time and property owner contact */}
            <Card
              sx={{
                mb: 3,
                border: "1px solid",
                borderColor: "info.light",
                bgcolor: "info.lighter",
              }}
            >
              <MDBox p={3}>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  textTransform="uppercase"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Ready for inspection
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
                  {property.readyForInspector === true
                    ? "Yes"
                    : property.readyForInspector === false
                    ? "No"
                    : "—"}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  textTransform="uppercase"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Preferred inspection date / time (from owner)
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
                  {property.preferredInspectionTime || "—"}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  textTransform="uppercase"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Property owner
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Name: {property.owner?.fullName || "—"}
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Phone: {property.owner?.phone || "—"}
                </MDTypography>
                <MDTypography variant="body2" color="text" sx={{ mb: 1 }}>
                  Email: {property.owner?.email || "—"}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary" fontStyle="italic">
                  If the preferred time is not available, contact the owner to reschedule.
                </MDTypography>
              </MDBox>
            </Card>

            {/* Assignment Form */}
            <Card>
              <MDBox p={3}>
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  mb={2}
                  display="flex"
                  alignItems="center"
                >
                  <Icon sx={{ mr: 1, color: "info.main" }}>assignment</Icon>
                  Inspection Details
                </MDTypography>
                <Divider sx={{ mb: 3 }} />

                {/* Inspector Selection */}
                <MDBox mb={3}>
                  <FormControl
                    fullWidth
                    required
                    disabled={submitting}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "44.3px",
                        borderRadius: 2,
                        backgroundColor: "white",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.02)",
                          "& fieldset": {
                            borderColor: "info.main",
                            borderWidth: 1.5,
                          },
                        },
                        "& fieldset": {
                          borderColor: "rgba(0, 0, 0, 0.23)",
                          transition: "border-color 0.2s ease",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "white",
                          "& fieldset": {
                            borderColor: "info.main",
                            borderWidth: 2,
                          },
                        },
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                          padding: "12px 14px",
                          boxSizing: "border-box",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        transform: selectedInspector
                          ? "translate(14px, -9px) scale(0.75)"
                          : "translate(14px, 16.5px) scale(1)",
                        transition: "transform 0.2s ease",
                        "&.Mui-focused": {
                          color: "info.main",
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                        "&.MuiInputLabel-shrink": {
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                      },
                    }}
                  >
                    <InputLabel id="inspector-select-label" shrink={!!selectedInspector}>
                      Select Inspector *
                    </InputLabel>
                    <Select
                      labelId="inspector-select-label"
                      value={selectedInspector || ""}
                      onChange={handleInspectorChange}
                      label="Select Inspector *"
                      displayEmpty={false}
                      notched={!!selectedInspector}
                      renderValue={(selected) => {
                        if (!selected || selected === "" || selected === "undefined") {
                          return "";
                        }
                        const selectedInspectorData = inspectors.find(
                          (insp) => String(insp._id || insp.id) === String(selected)
                        );
                        if (!selectedInspectorData) {
                          return "";
                        }
                        return (
                          <MDBox display="flex" alignItems="center" height="100%">
                            <MDBox
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 1.5,
                                bgcolor: "info.light",
                                mr: 1.5,
                              }}
                            >
                              <Icon
                                sx={{
                                  color: "info.main",
                                  fontSize: "1.25rem",
                                }}
                              >
                                assignment_ind
                              </Icon>
                            </MDBox>
                            <MDTypography variant="body2" fontWeight="medium" color="text.primary">
                              {selectedInspectorData.fullName}
                            </MDTypography>
                          </MDBox>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: 2,
                            mt: 1,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            maxHeight: 400,
                          },
                        },
                        anchorOrigin: {
                          vertical: "bottom",
                          horizontal: "left",
                        },
                        transformOrigin: {
                          vertical: "top",
                          horizontal: "left",
                        },
                      }}
                    >
                      {inspectors.length === 0 ? (
                        <MenuItem disabled value="">
                          <MDBox p={2} textAlign="center" width="100%">
                            <MDTypography variant="body2" color="text.secondary">
                              No inspectors available
                            </MDTypography>
                          </MDBox>
                        </MenuItem>
                      ) : (
                        inspectors.map((inspector) => {
                          const inspectorId = inspector._id || inspector.id;
                          const inspectorIdString = String(inspectorId);
                          const isSelected = inspectorIdString === String(selectedInspector);
                          return (
                            <MenuItem
                              key={inspectorIdString}
                              value={inspectorIdString}
                              selected={isSelected}
                              sx={{
                                px: 2,
                                py: 1.5,
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                                  "&:hover": {
                                    backgroundColor: "rgba(33, 150, 243, 0.12)",
                                  },
                                },
                              }}
                            >
                              <MDBox display="flex" alignItems="center" width="100%">
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    bgcolor: isSelected ? "info.light" : "grey.100",
                                    mr: 2,
                                    transition: "all 0.2s ease",
                                  }}
                                >
                                  <Icon
                                    sx={{
                                      color: isSelected ? "info.main" : "text.secondary",
                                      fontSize: "1.5rem",
                                    }}
                                  >
                                    assignment_ind
                                  </Icon>
                                </MDBox>
                                <MDBox flex={1}>
                                  <MDTypography
                                    variant="body2"
                                    fontWeight={isSelected ? "medium" : "regular"}
                                    color={isSelected ? "info.main" : "text.primary"}
                                  >
                                    {inspector.fullName}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.25 }}
                                  >
                                    {inspector.email}
                                  </MDTypography>
                                </MDBox>
                                {isSelected && (
                                  <Icon
                                    sx={{
                                      color: "info.main",
                                      ml: 1,
                                    }}
                                  >
                                    check_circle
                                  </Icon>
                                )}
                              </MDBox>
                            </MenuItem>
                          );
                        })
                      )}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Selected Inspector Info */}
                {selectedInspectorData && (
                  <MDBox
                    mb={3}
                    p={2}
                    sx={{
                      bgcolor: "info.lighter",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "info.main",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" mb={1}>
                      <Icon sx={{ mr: 1, color: "info.main" }}>check_circle</Icon>
                      <MDTypography
                        variant="caption"
                        color="info.main"
                        fontWeight="bold"
                        textTransform="uppercase"
                      >
                        Selected Inspector
                      </MDTypography>
                    </MDBox>
                    <MDBox display="flex" alignItems="center" mt={1}>
                      <Icon sx={{ mr: 1, color: "text.secondary" }}>person</Icon>
                      <MDBox>
                        <MDTypography variant="body2" fontWeight="medium" color="dark">
                          {selectedInspectorData.fullName}
                        </MDTypography>
                        <MDTypography variant="caption" color="text.secondary">
                          {selectedInspectorData.email}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                )}

                {/* Inspection Date */}
                <MDBox mb={3}>
                  <MDTypography
                    variant="caption"
                    fontWeight="medium"
                    color="text.secondary"
                    sx={{
                      mb: 0.5,
                      display: "block",
                      fontSize: "0.75rem",
                    }}
                  >
                    Inspection Date & Time *
                  </MDTypography>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    name="inspectionDate"
                    value={formData.inspectionDate}
                    onChange={handleChange}
                    disabled={submitting || !selectedInspector}
                    error={!!dateConflict}
                    helperText={
                      dateConflict
                        ? `Conflict: Inspector has another inspection at ${new Date(
                            dateConflict.inspectionDate
                          ).toLocaleString()}`
                        : "Select a date and time from the calendar or enter manually"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "44.3px",
                        "& input": {
                          padding: "12px 14px",
                          fontSize: "0.875rem",
                          width: "100%",
                          minWidth: "200px",
                        },
                        "& input[type='datetime-local']": {
                          "&::-webkit-calendar-picker-indicator": {
                            cursor: "pointer",
                            padding: "4px",
                          },
                        },
                        // Completely hide datetime-local format placeholder when empty and not focused
                        "&:not(.Mui-focused) input[type='datetime-local']:invalid": {
                          color: "transparent !important",
                          "&::-webkit-datetime-edit": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                          "&::-webkit-datetime-edit-fields-wrapper": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                          "&::-webkit-datetime-edit-text": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                          "&::-webkit-datetime-edit-month-field": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                          "&::-webkit-datetime-edit-day-field": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                          "&::-webkit-datetime-edit-year-field": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                          "&::-webkit-datetime-edit-hour-field": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                          "&::-webkit-datetime-edit-minute-field": {
                            opacity: "0 !important",
                            display: "none !important",
                          },
                        },
                        // Show content when focused
                        "&.Mui-focused input[type='datetime-local']": {
                          color: "text.primary !important",
                          "&::-webkit-datetime-edit": {
                            opacity: "1 !important",
                            display: "block !important",
                          },
                          "&::-webkit-datetime-edit-fields-wrapper": {
                            opacity: "1 !important",
                            display: "block !important",
                          },
                        },
                        // Show content when has value
                        "& input[type='datetime-local']:valid": {
                          color: "text.primary !important",
                          "&::-webkit-datetime-edit": {
                            opacity: "1 !important",
                            display: "block !important",
                          },
                          "&::-webkit-datetime-edit-fields-wrapper": {
                            opacity: "1 !important",
                            display: "block !important",
                          },
                        },
                      },
                    }}
                  />
                </MDBox>

                {/* Inspection Location */}
                <TextField
                  fullWidth
                  label="Inspection Location *"
                  name="inspectionLocation"
                  value={formData.inspectionLocation}
                  onChange={handleChange}
                  placeholder="Enter the exact location for inspection"
                  multiline
                  rows={4}
                  disabled={submitting}
                  helperText="Location address and Google Maps link will be auto-filled. You can edit if needed."
                  sx={{
                    mb: 3,
                    "& .MuiInputBase-root": {
                      fontSize: "0.875rem",
                    },
                  }}
                />

                {/* Submit Button */}
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={
                    submitting ||
                    !selectedInspector ||
                    !formData.inspectionDate ||
                    !formData.inspectionLocation.trim() ||
                    !!dateConflict
                  }
                  startIcon={submitting ? <CircularProgress size={20} /> : <Icon>check</Icon>}
                >
                  {submitting ? "Assigning..." : "Assign Inspector"}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          {/* Right Column - Calendar */}
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={3}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <MDTypography variant="h6" fontWeight="bold" display="flex" alignItems="center">
                    <Icon sx={{ mr: 1, color: "info.main" }}>calendar_today</Icon>
                    Inspector Schedule
                    {selectedInspectorData && (
                      <Chip
                        label={selectedInspectorData.fullName}
                        color="info"
                        size="small"
                        sx={{ ml: 2 }}
                      />
                    )}
                  </MDTypography>
                  {!selectedInspector && (
                    <Alert severity="info" sx={{ flex: 1, ml: 2 }}>
                      Please select an inspector to view their schedule
                    </Alert>
                  )}
                </MDBox>

                {selectedInspector ? (
                  <>
                    {/* Calendar Controls */}
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => navigateCalendar("PREV")}
                          startIcon={<Icon>chevron_left</Icon>}
                        >
                          Previous
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => setCalendarDate(new Date())}
                        >
                          Today
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => navigateCalendar("NEXT")}
                          endIcon={<Icon>chevron_right</Icon>}
                        >
                          Next
                        </MDButton>
                      </MDBox>
                      <MDBox display="flex" gap={1}>
                        <MDButton
                          variant={calendarView === "month" ? "contained" : "outlined"}
                          color="info"
                          size="small"
                          onClick={() => handleViewChange("month")}
                        >
                          Month
                        </MDButton>
                        <MDButton
                          variant={calendarView === "week" ? "contained" : "outlined"}
                          color="info"
                          size="small"
                          onClick={() => handleViewChange("week")}
                        >
                          Week
                        </MDButton>
                        <MDButton
                          variant={calendarView === "day" ? "contained" : "outlined"}
                          color="info"
                          size="small"
                          onClick={() => handleViewChange("day")}
                        >
                          Day
                        </MDButton>
                      </MDBox>
                    </MDBox>

                    {/* Calendar */}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "background.default",
                        borderRadius: 2,
                        height: 700,
                        overflow: "visible",
                        position: "relative",
                        "& .rbc-calendar": {
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                        },
                        "& .rbc-time-view": {
                          border: "none",
                        },
                        "& .rbc-time-header": {
                          border: "1px solid #e0e0e0",
                          "& .rbc-time-header-content": {
                            borderLeft: "1px solid #e0e0e0",
                          },
                        },
                        "& .rbc-time-content": {
                          borderTop: "2px solid #e0e0e0",
                          "& .rbc-time-gutter": {
                            "& .rbc-timeslot-group": {
                              minHeight: "60px",
                              borderBottom: "1px solid #f0f0f0",
                            },
                            "& .rbc-label": {
                              padding: "8px 12px",
                              fontSize: "0.875rem",
                              fontWeight: 500,
                            },
                          },
                          "& .rbc-day-slot": {
                            minHeight: "60px",
                            "& .rbc-time-slot": {
                              borderTop: "1px solid #f5f5f5",
                              minHeight: "60px",
                            },
                            "& .rbc-time-slot.rbc-now": {
                              backgroundColor: "rgba(25, 118, 210, 0.05)",
                            },
                          },
                        },
                        "& .rbc-month-view": {
                          "& .rbc-date-cell": {
                            padding: "8px",
                            minHeight: "80px",
                            "& > a": {
                              fontSize: "1rem",
                              fontWeight: 500,
                            },
                          },
                          "& .rbc-day-bg": {
                            minHeight: "120px",
                            border: "1px solid #e0e0e0",
                          },
                          "& .rbc-day-slot": {
                            minHeight: "120px",
                          },
                        },
                        "& .rbc-event": {
                          padding: "6px 10px",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          borderRadius: "4px",
                          minHeight: "24px",
                          lineHeight: "1.4",
                        },
                        // Truncate text for existing inspections only
                        "& .rbc-event[data-existing='true']": {
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        },
                        // For week view, ensure text doesn't overflow for existing inspections
                        "& .rbc-time-view .rbc-event[data-existing='true']": {
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        },
                        "& .rbc-event-label": {
                          fontSize: "0.75rem",
                          marginBottom: "2px",
                        },
                        "& .rbc-event-content": {
                          fontSize: "0.875rem",
                        },
                        "& .rbc-selected": {
                          backgroundColor: "#4CAF50 !important",
                          border: "2px solid #2e7d32 !important",
                          boxShadow: "0 2px 8px rgba(76, 175, 80, 0.4) !important",
                          zIndex: 10,
                        },
                        // Prevent existing inspections from being selected (green color)
                        "& .rbc-event[data-existing='true']": {
                          cursor: "pointer !important",
                          "&:hover": {
                            backgroundColor: "#1565c0 !important",
                            opacity: "1 !important",
                          },
                          "&.rbc-selected": {
                            backgroundColor: "#1976d2 !important",
                            border: "none !important",
                            boxShadow: "none !important",
                            opacity: "0.95 !important",
                          },
                        },
                        "& .rbc-slot-selection": {
                          backgroundColor: "rgba(76, 175, 80, 0.2) !important",
                          border: "2px dashed #4CAF50 !important",
                          zIndex: 5,
                        },
                        "& .rbc-slot-reserved": {
                          backgroundColor: "rgba(244, 67, 54, 0.1) !important",
                          border: "1px dashed rgba(244, 67, 54, 0.3) !important",
                          cursor: "not-allowed !important",
                          pointerEvents: "none !important",
                          opacity: 0.6,
                        },
                        "& .rbc-time-slot.rbc-slot-reserved": {
                          "&:hover": {
                            backgroundColor: "rgba(244, 67, 54, 0.15) !important",
                          },
                        },
                        "& .rbc-today": {
                          backgroundColor: "rgba(25, 118, 210, 0.05)",
                        },
                        "& .rbc-header": {
                          padding: "12px 8px",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          borderBottom: "2px solid #e0e0e0",
                          minHeight: "40px",
                        },
                        "& .rbc-agenda-view": {
                          "& .rbc-agenda-date-cell": {
                            padding: "12px",
                            minWidth: "120px",
                          },
                          "& .rbc-agenda-time-cell": {
                            padding: "12px",
                            minWidth: "100px",
                          },
                          "& .rbc-agenda-event-cell": {
                            padding: "12px",
                          },
                        },
                      }}
                    >
                      <Calendar
                        localizer={localizer}
                        events={getCalendarEvents()}
                        startAccessor="start"
                        endAccessor="end"
                        style={{ height: "100%", minHeight: "650px" }}
                        view={calendarView}
                        date={calendarDate}
                        onView={handleViewChange}
                        onNavigate={setCalendarDate}
                        onSelectEvent={handleSelectEvent}
                        onSelectSlot={handleSelectSlot}
                        selectable
                        eventPropGetter={eventStyleGetter}
                        slotPropGetter={slotPropGetter}
                        popup
                        showMultiDayTimes
                        step={60}
                        timeslots={1}
                        min={new Date(2024, 0, 1, 8, 0)}
                        max={new Date(2024, 0, 1, 20, 0)}
                        defaultView={
                          calendarView === "month"
                            ? "month"
                            : calendarView === "week"
                            ? "week"
                            : "day"
                        }
                        formats={{
                          timeGutterFormat: "HH:mm",
                          eventTimeRangeFormat: ({ start, end }) =>
                            `${localizer.format(start, "HH:mm", "en")} - ${localizer.format(
                              end,
                              "HH:mm",
                              "en"
                            )}`,
                          dayFormat: "ddd M/D",
                          dayHeaderFormat: "dddd M/D",
                          dayRangeFormat: ({ start, end }) =>
                            `${localizer.format(start, "M/D", "en")} - ${localizer.format(
                              end,
                              "M/D",
                              "en"
                            )}`,
                          agendaHeaderFormat: ({ start, end }) =>
                            `${localizer.format(start, "MMM DD", "en")} - ${localizer.format(
                              end,
                              "MMM DD",
                              "en"
                            )}`,
                          agendaDateFormat: "ddd MMM DD",
                          agendaTimeFormat: "HH:mm",
                          agendaTimeRangeFormat: ({ start, end }) =>
                            `${localizer.format(start, "HH:mm", "en")} - ${localizer.format(
                              end,
                              "HH:mm",
                              "en"
                            )}`,
                        }}
                        components={{
                          event: ({ event }) => {
                            // Custom event component to prevent selection on existing inspections
                            const isExisting = event.resource?.isExisting;
                            const isSelected = event.resource?.isSelected;
                            const eventStyle = eventStyleGetter(event);

                            // Only show hover modal for existing inspections
                            const handleMouseEnter = (e) => {
                              if (isExisting) {
                                // Get the position of the event element relative to the calendar container
                                const eventRect = e.currentTarget.getBoundingClientRect();
                                const calendarPaper =
                                  e.currentTarget.closest('[class*="MuiPaper"]');
                                const calendarRect = calendarPaper?.getBoundingClientRect();

                                if (calendarRect) {
                                  // Calculate position relative to the calendar Paper container
                                  // Position above the slot (top of event minus modal height)
                                  const top = eventRect.top - calendarRect.top - 8; // 8px gap above
                                  const left = eventRect.left - calendarRect.left;

                                  setModalPosition({ top, left });
                                }

                                setHoveredEvent({
                                  type: "existing",
                                  inspection: event.resource,
                                  start: event.start,
                                  end: event.end,
                                });
                                setHoverModalOpen(true);
                              }
                            };

                            const handleMouseLeave = () => {
                              if (isExisting) {
                                setHoverModalOpen(false);
                              }
                            };

                            return (
                              <div
                                data-existing={isExisting ? "true" : "false"}
                                data-selected={isSelected ? "true" : "false"}
                                style={{
                                  ...eventStyle.style,
                                  cursor: isExisting
                                    ? "pointer"
                                    : isSelected
                                    ? "pointer"
                                    : "default",
                                  width: "100%",
                                  height: "100%",
                                }}
                                onClick={(e) => {
                                  if (isExisting) {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleSelectEvent(event);
                                  }
                                }}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                                onMouseDown={(e) => {
                                  if (isExisting) {
                                    e.stopPropagation();
                                    e.preventDefault();
                                  }
                                }}
                                title={
                                  isExisting
                                    ? `${String(
                                        event.title || "Existing Inspection"
                                      )} - Hover to view details`
                                    : String(event.title || "Inspection")
                                }
                              >
                                {event.title && String(event.title).trim() !== "" ? (
                                  <span
                                    style={{
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      display: "block",
                                      width: "100%",
                                    }}
                                  >
                                    {String(event.title)}
                                  </span>
                                ) : null}
                              </div>
                            );
                          },
                        }}
                      />

                      {/* Chat-style Modal for Inspection Details - positioned relative to calendar */}
                      {hoverModalOpen && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: `${modalPosition.top}px`,
                            left: `${modalPosition.left}px`,
                            zIndex: 9999,
                            minWidth: "320px",
                            maxWidth: "400px",
                            pointerEvents: "auto",
                            transform: "translateY(-100%)",
                            animation: "fadeInDown 0.2s ease-in",
                            "@keyframes fadeInDown": {
                              from: {
                                opacity: 0,
                                transform: "translateY(calc(-100% - 10px))",
                              },
                              to: {
                                opacity: 1,
                                transform: "translateY(-100%)",
                              },
                            },
                          }}
                          onMouseLeave={() => setHoverModalOpen(false)}
                        >
                          {/* Chat bubble content */}
                          <Paper
                            elevation={8}
                            sx={{
                              bgcolor: "white",
                              color: "text.primary",
                              borderRadius: 2,
                              p: 2,
                              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                              position: "relative",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            {hoveredEvent?.type === "existing" && hoveredEvent.inspection ? (
                              <MDBox>
                                <MDBox
                                  mb={1.5}
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ color: "text.primary", fontSize: "1rem" }}
                                    display="flex"
                                    alignItems="center"
                                  >
                                    <Icon sx={{ mr: 0.5, fontSize: "1.2rem", color: "info.main" }}>
                                      info
                                    </Icon>
                                    Inspection Details
                                  </MDTypography>
                                </MDBox>
                                <Divider sx={{ bgcolor: "#e0e0e0", mb: 1.5 }} />
                                <MDBox mb={1.5}>
                                  <MDTypography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                    }}
                                    display="block"
                                    mb={0.5}
                                  >
                                    Property
                                  </MDTypography>
                                  <MDTypography
                                    variant="body2"
                                    sx={{ color: "text.primary", fontWeight: 600 }}
                                  >
                                    {hoveredEvent.inspection.title || "N/A"}
                                  </MDTypography>
                                </MDBox>
                                <MDBox mb={1.5}>
                                  <MDTypography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                    }}
                                    display="block"
                                    mb={0.5}
                                  >
                                    Date & Time
                                  </MDTypography>
                                  <MDTypography variant="body2" sx={{ color: "text.primary" }}>
                                    {hoveredEvent.start
                                      ? `${new Date(hoveredEvent.start).toLocaleDateString(
                                          "en-US",
                                          {
                                            weekday: "short",
                                            month: "short",
                                            day: "numeric",
                                          }
                                        )}`
                                      : "N/A"}
                                  </MDTypography>
                                  <MDTypography
                                    variant="caption"
                                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                                  >
                                    {hoveredEvent.start && hoveredEvent.end
                                      ? `${new Date(hoveredEvent.start).toLocaleTimeString(
                                          "en-US",
                                          {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          }
                                        )} - ${new Date(hoveredEvent.end).toLocaleTimeString(
                                          "en-US",
                                          {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          }
                                        )}`
                                      : "N/A"}
                                  </MDTypography>
                                </MDBox>
                                <MDBox mb={1.5}>
                                  <MDTypography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                    }}
                                    display="block"
                                    mb={0.5}
                                  >
                                    Status
                                  </MDTypography>
                                  <Chip
                                    label={hoveredEvent.inspection.status || "N/A"}
                                    size="small"
                                    color={
                                      ["Inspection", "First_Inspection"].includes(
                                        hoveredEvent.inspection.status
                                      )
                                        ? "info"
                                        : ["Inspection_Offer", "Inspection_Completed"].includes(
                                            hoveredEvent.inspection.status
                                          )
                                        ? "success"
                                        : "default"
                                    }
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: "24px",
                                    }}
                                  />
                                </MDBox>
                                {hoveredEvent.inspection.location && (
                                  <MDBox mb={1.5}>
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                      }}
                                      display="block"
                                      mb={0.5}
                                    >
                                      Location
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        color: "text.primary",
                                        fontSize: "0.75rem",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        display: "block",
                                      }}
                                    >
                                      {hoveredEvent.inspection.location.length > 100
                                        ? `${hoveredEvent.inspection.location.substring(0, 100)}...`
                                        : hoveredEvent.inspection.location}
                                    </MDTypography>
                                  </MDBox>
                                )}
                                {hoveredEvent.inspection.address && (
                                  <MDBox>
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        fontWeight: 600,
                                        fontSize: "0.7rem",
                                      }}
                                      display="block"
                                      mb={0.5}
                                    >
                                      Address
                                    </MDTypography>
                                    <MDTypography
                                      variant="caption"
                                      sx={{ color: "text.primary", fontSize: "0.75rem" }}
                                    >
                                      {hoveredEvent.inspection.address}
                                      {hoveredEvent.inspection.city
                                        ? `, ${hoveredEvent.inspection.city}`
                                        : ""}
                                    </MDTypography>
                                  </MDBox>
                                )}
                              </MDBox>
                            ) : null}
                          </Paper>

                          {/* Chat bubble arrow pointing down */}
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: "-8px",
                              left: "20px",
                              width: 0,
                              height: 0,
                              borderLeft: "8px solid transparent",
                              borderRight: "8px solid transparent",
                              borderTop: "8px solid white",
                              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                            }}
                          />
                        </Box>
                      )}
                    </Paper>

                    {/* Legend */}
                    <MDBox mt={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 20, height: 20, bgcolor: "#1976d2", borderRadius: 1 }} />
                        <MDTypography variant="caption">Existing Inspections</MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            bgcolor: "#4CAF50",
                            borderRadius: 1,
                            border: "2px solid #2e7d32",
                          }}
                        />
                        <MDTypography variant="caption">Selected Time</MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            bgcolor: "rgba(244, 67, 54, 0.1)",
                            borderRadius: 1,
                            border: "1px dashed rgba(244, 67, 54, 0.3)",
                          }}
                        />
                        <MDTypography variant="caption">Reserved/Unavailable</MDTypography>
                      </MDBox>
                    </MDBox>
                  </>
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                    <MDTypography variant="body1" color="text">
                      Select an inspector to view their schedule
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default AssignInspector;
